"use client";

import type { AuthUserInfo } from "@/auth";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/app/_components/ui/alert-dialog";
import {
  getTimezoneLabel,
  isBrowserSyncedTimezoneUserType,
  isTimezonePromptDismissed,
  resolveTimezoneSyncAction,
} from "@/lib/datetime/timezones";
import { isSupportedTimezone } from "@marble/core/modules/timezones";
import { updateOwnTimezone } from "@/lib/modules/users/actions";
import { useTRPC } from "@/trpc/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSession } from "next-auth/react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

/**
 * Detects the browser's IANA timezone once per session for the signed-in user
 * and reconciles it against their stored `User.timezone`:
 * - stored is null -> silently backfill it (rollout auto-fill)
 * - stored differs from a supported detected zone -> confirm before overwriting
 * - detected is unsupported or already matches -> do nothing
 *
 * Runs for staff and school workers only — see
 * IS_BROWSER_SYNCED_TIMEZONE_USER_TYPE. Students and guardians store the zone
 * their care happens in, not the one their device is in, so they are never
 * synced or prompted.
 *
 * Renders nothing except the (rare) confirmation dialog. Skipped while
 * impersonating so we never write another user's zone onto an admin session.
 */
export function BrowserTimezoneSync() {
  const { data: session } = useSession();
  const authUser = session?.user as AuthUserInfo | undefined;
  const isImpersonating = session?.user?.isImpersonating ?? false;
  const userId = authUser?.id;

  const trpc = useTRPC();
  const queryClient = useQueryClient();

  // Detect the browser zone during render, client-only, via a lazy initializer
  // (no effect → no synchronous setState in an effect). SSR yields null, which
  // is safe because nothing rendered depends on it (the dialog stays closed).
  const [detectedZone] = useState<string | null>(() =>
    typeof window === "undefined"
      ? null
      : // The one place that legitimately reads the browser's live zone:
        // this component exists to write it to `User.timezone`, which is
        // then what every render reads instead.
        // eslint-disable-next-line marble/no-unzoned-date-render -- the source of the stored zone
        Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  // Flipped once the user resolves the prompt so the derived open state closes
  // and does not reopen.
  const [isPromptClosed, setIsPromptClosed] = useState(false);
  // Set when the user makes an explicit choice (update or keep) so the
  // catch-all close handler doesn't double-record a dismissal or stamp a
  // stray cooldown after an update.
  const didResolveRef = useRef(false);
  // One silent backfill write per mount, whatever happens to it. The
  // sessionStorage marker alone can't guarantee that: anything that clears it
  // (or storage being unavailable) lets the effect fire again.
  const didAttemptSilentBackfillRef = useRef(false);

  const isEligible =
    userId != null &&
    isBrowserSyncedTimezoneUserType(authUser?.userType) &&
    !isImpersonating &&
    detectedZone != null &&
    isSupportedTimezone(detectedZone) &&
    !_isChecked(userId);

  const timezoneQuery = useQuery({
    ...trpc.users.getMyTimezone.queryOptions(),
    enabled: isEligible,
  });

  const { mutate } = useMutation({
    mutationFn: (zone: string) => updateOwnTimezone(zone),
    onSuccess: () => {
      void queryClient.invalidateQueries(
        trpc.users.getMyTimezone.queryOptions(),
      );
    },
  });

  const action =
    isEligible && detectedZone != null && timezoneQuery.isSuccess
      ? resolveTimezoneSyncAction(timezoneQuery.data ?? null, detectedZone)
      : "none";

  // Once the query resolves, mark this session checked so we don't re-query on
  // every new tab. `prompt` is left unmarked so the dialog can show — it marks
  // itself checked in its handlers. `set-silent` also fires the backfill write,
  // at most once per mount and with no rollback on failure: the write can fail
  // permanently (e.g. the session's User row is gone), and retrying it floods
  // the server. A failed backfill retries on the user's next session.
  // This effect only performs external writes (DB mutation + sessionStorage) and
  // never calls a React state setter, so it can't trigger cascading renders.
  useEffect(() => {
    if (
      !timezoneQuery.isSuccess ||
      action === "prompt" ||
      userId == null ||
      detectedZone == null
    ) {
      return;
    }
    // Fresh read guards React StrictMode's double-invoke from writing twice.
    if (_isChecked(userId)) {
      return;
    }
    _markChecked(userId);
    if (action === "set-silent" && !didAttemptSilentBackfillRef.current) {
      didAttemptSilentBackfillRef.current = true;
      mutate(detectedZone);
    }
  }, [action, userId, detectedZone, mutate, timezoneQuery.isSuccess]);

  const shouldPrompt =
    action === "prompt" &&
    userId != null &&
    detectedZone != null &&
    !isPromptClosed &&
    !_isDismissed(userId, detectedZone);

  const handleUpdate = () => {
    if (userId == null || detectedZone == null) {
      return;
    }
    didResolveRef.current = true;
    _markChecked(userId);
    setIsPromptClosed(true);
    mutate(detectedZone, {
      onSuccess: () => toast.success("Timezone updated"),
      onError: (error) => {
        // Roll back so the prompt can reappear next session if the write failed.
        _clearChecked(userId);
        toast.error(
          error instanceof Error ? error.message : "Failed to update timezone",
        );
      },
    });
  };

  const handleCancel = () => {
    if (userId != null && detectedZone != null) {
      didResolveRef.current = true;
      _markDismissed(userId, detectedZone);
      _markChecked(userId);
    }
    setIsPromptClosed(true);
  };

  const storedLabel = getTimezoneLabel(timezoneQuery.data ?? null);
  const detectedLabel = getTimezoneLabel(detectedZone);

  return (
    <AlertDialog
      open={shouldPrompt}
      onOpenChange={(open) => {
        if (open) {
          return;
        }
        // ESC / backdrop close without an explicit choice counts as a
        // dismissal, so we honor the cooldown instead of re-prompting next
        // session. Explicit gestures set the ref and record their own state.
        if (!didResolveRef.current && userId != null && detectedZone != null) {
          _markDismissed(userId, detectedZone);
          _markChecked(userId);
        }
        setIsPromptClosed(true);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Update your timezone?</AlertDialogTitle>
          <AlertDialogDescription>
            {`Your device is in ${detectedLabel}, but your account is set to ${storedLabel}. Update your timezone so times show correctly?`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancel}>
            Keep {storedLabel}
          </AlertDialogCancel>
          <AlertDialogAction onClick={handleUpdate}>
            Update timezone
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

const _checkedKey = (userId: number): string => `marble.tzChecked.${userId}`;
const _dismissedKey = (userId: number, zone: string): string =>
  `marble.tzDismissed.${userId}.${zone}`;

function _isChecked(userId: number): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    return window.sessionStorage.getItem(_checkedKey(userId)) === "1";
  } catch {
    return false;
  }
}

function _markChecked(userId: number): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.sessionStorage.setItem(_checkedKey(userId), "1");
  } catch {
    // Storage may be unavailable (private mode, quota); worst case we re-check.
  }
}

function _clearChecked(userId: number): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.sessionStorage.removeItem(_checkedKey(userId));
  } catch {
    // Storage may be unavailable; worst case we skip the retry this session.
  }
}

function _isDismissed(userId: number, zone: string): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    return isTimezonePromptDismissed(
      window.localStorage.getItem(_dismissedKey(userId, zone)),
    );
  } catch {
    return false;
  }
}

function _markDismissed(userId: number, zone: string): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(
      _dismissedKey(userId, zone),
      String(Date.now()),
    );
  } catch {
    // Storage may be unavailable; worst case we prompt again next session.
  }
}
